<template>
	<div class="landing" :class="`landing_background_${background}`">
		<div v-if="stepLayout" class="landing__layout container-fluid">
			<!-- eslint-disable vue/require-v-for-key -->
			<template v-for="(layoutItem, index) in stepLayout">
				<component
					:key="index"
					:is="layoutItem.component"
					v-on="layoutItem.handlers"
					v-bind="layoutComponentsProps[layoutItem.component]"
				/>
			</template>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'

import { ANALYTIC_AGE_TO_AGE_RANGE_MAP } from '@/helpers/constants'
import { ENV_DEV_OR_STAGING, VUE_APP_VERSION } from '@/helpers/environment'
import { os, version, browserEngine, browserName, browserVersion } from '@/helpers/device'
import { findMatchingOverride } from '@/helpers/jsonConfig'
import { mergeDeep } from '@/helpers/utils'

import { getAppAreaExperimentPart } from '@/funnels/funnels.ts'
import Jet from '@/modules/Jet'
import Sentry from '@/modules/Sentry'

import landingComponentsList from '@/modules/Landing/components'
import { instances } from '@/modules/Landing/landingInstances.ts'

const componentsMap = landingComponentsList.reduce((componentsMap, component) => {
	componentsMap[component.name] = component
	return componentsMap
}, {})

const emitsMap = landingComponentsList.reduce((emitsMap, component) => {
	emitsMap[component.name] = component.emits ?? []
	return emitsMap
}, {})

export default {
	name: 'LandingView',

	components: componentsMap,

	data() {
		return {
			step: 0,
			schemas: [],
			isCreativeOverride: false,
			handlersMap: {
				progressLoaderStarted: this.onProgressLoaderStarted,
				progressLoaderCompleted: this.onProgressLoaderCompleted,
				ageSelected: this.onAgeSelected,
			},
		}
	},

	computed: {
		...mapGetters({
			language: 'i18n/getLanguage',
			customLanding: 'i18n/getCustomLanding',
			countryCode: 'getCountryCode',
			appAreaConfig: 'location/getAppAreaConfig',
			getQueryParam: 'location/getQueryParam',
			hostname: 'location/hostname',
			onetrustActiveGroups: 'onetrust/getActiveGroups',
		}),

		currentStepSchema() {
			return this.schemas[this.step]
		},

		background() {
			return this.currentStepSchema?.config?.background ?? 'base'
		},

		stepLayout() {
			return this.currentStepSchema?.order.map((layoutComponentName) => {
				return {
					component: layoutComponentName,
					handlers: this.getComponentHandlersMapByComponentName(layoutComponentName),
				}
			})
		},

		layoutComponentsProps() {
			const ctxParams = {
				language: this.language,
			}

			if (this.currentStepSchema.config?.dynamicLandingType) {
				ctxParams.dynamicLandingData = this.getDynamicLandingData(
					this.currentStepSchema.config.dynamicLandingType,
					this.getQueryParam('lpid'),
				)
			}

			return this.stepLayout.reduce((props, layoutComponent) => {
				const layoutComponentName = layoutComponent.component

				let propsGenerator = this.currentStepSchema.components[layoutComponentName]

				if (typeof propsGenerator === 'function') {
					props[layoutComponentName] = propsGenerator({
						ctx: ctxParams,
						$t: this.$t.bind(this),
						$tc: this.$tc.bind(this),
						$n: this.$n.bind(this),
						$te: this.$te.bind(this),
						$tp: this.$tp.bind(this),
					})
				}

				return props
			}, {})
		},
	},

	watch: {
		'$route.query.step': {
			handler(newStep) {
				const stepNumber = parseInt(newStep) || 0
				if (stepNumber !== this.step) {
					this.step = stepNumber
				}
			},

			immediate: true,
		},
	},

	async created() {
		const appAreaExperimentLandingPart = getAppAreaExperimentPart(
			this.appAreaConfig.appArea,
			'landing',
			this.$absmartly,
		)
		let landingIds = this.getCurrentLandingIds()
		if (appAreaExperimentLandingPart) {
			if (Array.isArray(appAreaExperimentLandingPart.ids)) {
				landingIds = appAreaExperimentLandingPart.ids
			}
		}

		if (!landingIds || landingIds.length === 0) {
			Sentry.captureMessage('Landing ids not found')
			return this.$router.push({ name: 'landing' })
		}

		let schemas = landingIds
			.map((id) => {
				const configModule = instances[id]
				if (!configModule) {
					Sentry.captureMessage(`Landing config not found for id: ${id}`)
					return null
				}
				return Object.freeze(configModule)
			})
			.filter(Boolean)

		if (appAreaExperimentLandingPart?.experiment) {
			const experimentModule = await appAreaExperimentLandingPart.experiment()
			const experimentalConfig = experimentModule.default(schemas)

			schemas = experimentalConfig
		}

		this.schemas = schemas

		if (this.schemas.length === 0) {
			Sentry.captureMessage('No valid landing schemas found')
			return this.$router.push({ name: 'landing' })
		}

		this.updateLocalFlags({ funnelFeatureFlag: this.currentStepSchema?.config?.funnelFeatureFlag ?? null })
		this.$store.commit('location/setInitialFunnelType', this.appAreaConfig.funnel.slug)
		// Track experiment exposure for web_simple_web_app_intro
		this.resetAnalyticDataCounter()
		this.trackAnalyticsShowScreenEvents()
	},

	beforeDestroy() {
		window.dataLayer?.push({
			event: 'updateGtagData',
			tracking_enabled: true,
		})
	},

	methods: {
		...mapMutations({
			resetAnalyticDataCounter: 'survey/resetAnalyticDataCounter',
			updateProperty: 'survey/updateProperty',
			updateSurveyModel: 'survey/updateSurveyModel',
			updateLocalFlags: 'userFlags/updateLocalFlags',
		}),

		getCurrentLandingIds() {
			const matchingIds = findMatchingOverride(this.appAreaConfig.landing.overrides, {
				...this.$store.state.location.queryParams,
				language: this.language,
				country: this.countryCode,
			})

			if (Array.isArray(matchingIds)) {
				this.isCreativeOverride = true
				return matchingIds
			}

			return this.appAreaConfig.landing.ids
		},

		getComponentHandlersMapByComponentName(componentName) {
			const componentEventsList = emitsMap[componentName]
			return componentEventsList
				.filter((eventName) => typeof this.handlersMap[eventName] === 'function')
				.reduce((componentHandlersMap, eventName) => {
					componentHandlersMap[eventName] = this.handlersMap[eventName]
					return componentHandlersMap
				}, {})
		},

		getDynamicLandingData(dynamicLandingType, lpid) {
			const configDynamicLandingData = Object.freeze(this.customLanding.lpid[dynamicLandingType]?.[lpid] ?? {})

			if (ENV_DEV_OR_STAGING) {
				return mergeDeep(
					{ ...configDynamicLandingData },
					{
						title: this.$route.query.lp_title ?? configDynamicLandingData.title,
						subtitle: this.$route.query.lp_subtitle ?? configDynamicLandingData.subtitle,
						loader: {
							label: this.$route.query.lp_loader_label ?? configDynamicLandingData.loader?.label,
							note: this.$route.query.lp_loader_note ?? configDynamicLandingData.loader?.note,
						},
						feature_description:
							this.$route.query.lp_feature_description ?? configDynamicLandingData.feature_description,
						disclaimer: this.$route.query.lp_disclaimer ?? configDynamicLandingData.disclaimer,
						image_url: this.$route.query.lp_image_url ?? configDynamicLandingData.image_url,
						animation_url: this.$route.query.lp_animation_url ?? configDynamicLandingData.animation_url,
					},
				)
			}

			return configDynamicLandingData
		},

		setAmplitudeUserProperties() {
			const utmCampaign = this.getQueryParam('utm_campaign')
				? decodeURIComponent(this.getQueryParam('utm_campaign'))
				: null
			const utmContent = this.getQueryParam('utm_content')
				? decodeURIComponent(this.getQueryParam('utm_content'))
				: null
			const utmTerm = this.getQueryParam('utm_term') ? decodeURIComponent(this.getQueryParam('utm_term')) : null

			return this.$analytic.setUserProperties({
				Device: { os, version, height: window.innerHeight, width: window.innerWidth },
				browserEngine,
				browserName,
				browserVersion,
				SurveyVersion: VUE_APP_VERSION,
				locale: this.$i18n.locale,
				CookieConsentActiveGroups: this.onetrustActiveGroups,
				hostname: this.hostname,
				from: this.getQueryParam('from'),
				AdgroupId: this.getQueryParam('adgroup_id'),
				AdId: this.getQueryParam('ad_id'),
				CampaignId: this.getQueryParam('campaign_id'),
				UtmGender: this.getQueryParam('utm_gender'),
				UtmMedium: this.getQueryParam('utm_medium'),
				UtmPartnerId: this.getQueryParam('utm_partner_id'),
				UtmSource: this.getQueryParam('utm_source'),
				UtmCampaign: utmCampaign,
				UtmContent: utmContent,
				UtmTerm: utmTerm,
			})
		},

		trackAnalyticsShowScreenEvents() {
			if (this.step === 0) {
				// We set user properties only on the first landing. And waiting for the setting. It's a temporary solution for Amplitude.
				this.setAmplitudeUserProperties().then(() => {
					this.$analytic.logEvent('ScreenView', { title: 'OnboardingWelcomeScreen' })
					this.$analytic.logEvent('OnboardingStart', { from: this.getQueryParam('from') })
				})
				Jet.logCoreEvent('OnboardingStart')
			}
		},

		trackAnalyticsDoneScreenEvents() {
			if (this.step === 0) {
				this.$analytic.logEvent('OnboardingWelcomeScreenDone', { data: 'lose_weight' })
			}
		},

		goNext() {
			this.trackAnalyticsDoneScreenEvents()

			if (this.step < this.schemas.length - 1) {
				this.$router.push({
					query: {
						...this.$route.query,
						step: this.step + 1,
					},
				})
			} else {
				this.$router.push({ name: this.appAreaConfig.funnel.route.name })
			}
		},

		onProgressLoaderStarted() {},

		onProgressLoaderCompleted() {
			if (ENV_DEV_OR_STAGING && this.$route.query.loader_pause) {
				return
			}
			this.goNext()
		},

		onAgeSelected(ageRange) {
			this.updateSurveyModel({
				...this.$store.state.survey.model,
				age: [ageRange.value.toString()],
			})

			this.updateProperty({ property: 'age', value: ageRange.value, isData: true })
			this.$absmartly.attributes({ age_range: ANALYTIC_AGE_TO_AGE_RANGE_MAP[ageRange.value] })

			this.$analytic.setUserProperties({ 'Age Range': ageRange.analyticValue })
			this.$analytic.logEvent('OnboardingAgeRangeScreenDone', { data: ageRange.analyticValue })

			this.goNext()
		},
	},
}
</script>

<style lang="scss" scoped>
.landing {
	&_background {
		&_base {
			background-color: var(--Background-Base);
		}

		&_accent {
			background-color: var(--Fill-Accent-Dimmed);
		}

		&_transparent {
			background-color: transparent;
		}
	}

	&__layout {
		min-height: 100vh;
		padding-bottom: 120px; // 104px cookie banner + 16px padding
		overflow: auto; // Fix to prevent the margin-top of the first element from extending beyond the screen

		&:has(.glp1) {
			padding-bottom: 0;
			overflow: hidden;
		}
	}
}
</style>
